import { Component, inject, signal } from '@angular/core';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { ApiService } from '../../api.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  newProgressDialogStep,
  openProgressDialog,
  ProgressDialogStep,
  ProgressDialogStepState,
} from '../../components/progressDialog/progressDialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { TAGTodo, TodoPriority, TodoStatus } from '../../../models/tagtodo';
import { TAGUser } from '../../../models/taguser';
import { MessageService } from 'primeng/api';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../components/action-confirmation/action-confirmation-dialog';
import { SelectModule } from 'primeng/select';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';

export interface TodoEditDialogData {
  todo: TAGTodo;
}

@Component({
  selector: 'app-internal-todo-edit-dialog',
  imports: [
    TableModule,
    SkeletonModule,
    InputTextModule,
    DialogModule,
    ButtonModule,
    FluidModule,
    ReactiveFormsModule,
    FloatLabelModule,
    TextareaModule,
    DatePickerModule,
    TranslatePipe,
    ToggleButtonModule,
    SelectModule,
    AutoCompleteModule
  ],
  providers: [DialogService],
  templateUrl: './todoEditDialog.html',
  styleUrl: './todoEditDialog.scss',
})
export class TodoEditDialog {
  private dialogService = inject(DialogService);
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);
  public api = inject(ApiService);

  todoForm = new FormGroup({
    status: new FormControl(TodoStatus.OPEN, [Validators.required]),
    priority: new FormControl(TodoPriority.NORMAL, [Validators.required]),
    description: new FormControl('', [Validators.required]),
    duedate: new FormControl(new Date()),
    notification: new FormControl(new Date()),
  });

  assigneeControl = new FormControl<TAGUser>(undefined);

  todoIsNew: boolean = true;
  todo: TAGTodo = new TAGTodo();

  users = signal<TAGUser[]>([]);
  loadingUsers = signal(true);
  filteredUsers = signal<TAGUser[]>([]);

  get allStatus() {
    return TodoStatus.ALL;
  }

  get allPriorities() {
    return TodoPriority.ALL;
  }

  constructor(
    public dialogRef: DynamicDialogRef<TodoEditDialog>,
    public config: DynamicDialogConfig<TodoEditDialogData>,
  ) {
    if (config.data?.todo) {
      this.todo = new TAGTodo(Object.assign({}, config.data.todo.serialize()));
      this.todoForm.patchValue(this.todo);
    } else {
      this.todo = new TAGTodo();
      this.todoForm.reset();
    }
    this.todoIsNew = this.todo._id == undefined;

    if (!this.todoIsNew) {
      config.header = this.translate.translate('internal.todos.editTitle')();
    } else {
      config.header = this.translate.translate('internal.todos.editTitleNew')();
    }
  }

  ngOnInit(): void {
    this.api.waitForAPI().then(() => {
      this.api
        .getUsers()
        .then((users: TAGUser[]) => {
          this.users.set(users);
          this.assigneeControl.setValue(users.find((u) => u._id === this.todo.assignee));
          this.loadingUsers.set(false);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.translate('errors.couldNotLoadUsers')(),
            detail: reason.message,
          });
        });
    });
  }

  _searchUser(event: AutoCompleteCompleteEvent) {
    this.filteredUsers.set(
      this.users().filter(
        (user) =>
          user.username.toLowerCase().includes(event.query.toLowerCase()) ||
          user.firstname.toLowerCase().includes(event.query.toLowerCase()) ||
          user.lastname.toLowerCase().includes(event.query.toLowerCase()) ||
          user.fullname.toLowerCase().includes(event.query.toLowerCase()) ||
          user.email.toLowerCase().includes(event.query.toLowerCase()),
      ),
    );

    console.log(event.query, this.filteredUsers());
  }

  deleteTodo() {
    if (this.todoIsNew) return;

    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.todos.deleteTitle')(),
        this.translate.translate('internal.todos.deleteMessage')(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate('internal.todos.deleteProgress')()),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deleteTodo(this.todo._id);
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }

  closeDialog(save?: boolean) {
    if (!save) {
      this.dialogRef.close(false);
      return;
    }

    if (!this.todoForm.valid) return;

    let updateStep = newProgressDialogStep(
      new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate('internal.todos.updateProgress')()),
    );

    if (this.todoIsNew) {
      updateStep = newProgressDialogStep(
        new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate('internal.todos.createProgress')()),
      );
    }

    let progressDialog = openProgressDialog(this.dialogService, [updateStep]);

    progressDialog.onChildComponentLoaded.subscribe((pDialog) => {
      updateStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

      this.todo.deserialize(this.todoForm.value);
      this.todo.assignee = this.assigneeControl.value._id;

      if (this.todoIsNew) {
        this.api
          .newTodo(this.todo)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      } else {
        this.api
          .updateTodo(this.todo)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      }
    });
  }
}
